import assert from 'assert';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';

import {
  MultiProcedureRunOrPlanListingTableQuery_study_StudyNode_activeProcedureRunsAndProcedureRunPlans_ProcedureRunNode as AccessibleProcedureRunNode,
  MultiProcedureRunOrPlanListingTableQuery_study_StudyNode_activeProcedureRunsAndProcedureRunPlans_ProcedureRunPlanNode as AccessibleProcedureRunPlanNode,
} from './__generated/MultiProcedureRunOrPlanListingTableQuery';
import type {ProcedureRunOverview, ProcedureRunPlanOverview} from './MultiProcedureRunAndPlanListingTable';

interface LegacyFlowchartVersionWithParentName {
  __typename: 'ProcedureFlowchartVersionNode';
  procedureFlowchart?: {
    parent?: {
      name?: string | null;
    } | null;
  } | null;
}

interface ProxyFlowchartVersionWithProcedureName {
  __typename: 'ProcedureFlowchartConfigVersionProxyNode';
  procedure?: {
    name?: string | null;
  } | null;
}

const PLACEHOLDER_PLAN_NAME = 'New run plan';

export function getProcedureRunPlanNameFromTemplateOrProxyVersion<
  T extends LegacyFlowchartVersionWithParentName | ProxyFlowchartVersionWithProcedureName,
>(templateOrProxyVersion: T | null | undefined): string {
  if (templateOrProxyVersion == null) {
    return PLACEHOLDER_PLAN_NAME;
  }
  if (templateOrProxyVersion.__typename === 'ProcedureFlowchartVersionNode') {
    return templateOrProxyVersion.procedureFlowchart?.parent?.name ?? '';
  }
  if (templateOrProxyVersion.__typename === 'ProcedureFlowchartConfigVersionProxyNode') {
    return templateOrProxyVersion.procedure?.name ?? '';
  }
  throw new Error('Invalid template or proxy version');
}

export function getProcedureRunOverview(
  procedureRun: AccessibleProcedureRunNode
): ProcedureRunOverview | null {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun({
    ...procedureRun,
    templateProcedureFlowchartVersion: procedureRun.runTemplateProcedureFlowchartVersion,
    procedureFlowchartConfigVersionProxy: procedureRun.procedureFlowchartConfigVersionProxy,
  });
  if (isInaccessible(flowchartVersion)) {
    return null;
  }
  const procedureParent = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion);
  return {
    ...procedureRun,
    procedureTemplateId: procedureParent.id,
    flowchartVersionId: getFlowchartVersionIdForNavigation(flowchartVersion),
  };
}

export function getProcedureRunPlanName(
  procedureRunPlan: Pick<
    AccessibleProcedureRunPlanNode,
    '__typename' | 'templateProcedureFlowchartVersion' | 'procedureFlowchartConfigVersionProxy'
  >
): string {
  const flowchartOrProxyVersion =
    getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  if (flowchartOrProxyVersion == null) {
    return PLACEHOLDER_PLAN_NAME;
  }
  if (isInaccessible(flowchartOrProxyVersion)) {
    throw new Error('Expected flowchart or proxy version to be accessible.');
  }
  return getProcedureRunPlanNameFromTemplateOrProxyVersion(
    flowchartOrProxyVersion as LegacyFlowchartVersionWithParentName | ProxyFlowchartVersionWithProcedureName
  );
}

export function getProcedureRunPlanOverview(
  procedureRunPlan: AccessibleProcedureRunPlanNode
): ProcedureRunPlanOverview | null {
  // Procedure run plans will use the template procedure flowchart version (legacy) or the procedure flowchart
  // config version proxy (pow) to get the procedure name
  if (
    (procedureRunPlan.templateProcedureFlowchartVersion != null &&
      isInaccessible(procedureRunPlan.templateProcedureFlowchartVersion)) ||
    (procedureRunPlan.procedureFlowchartConfigVersionProxy != null &&
      isInaccessible(procedureRunPlan.procedureFlowchartConfigVersionProxy))
  ) {
    return null;
  }
  const flowchartOrProxyVersion =
    getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  // Using the procedure template name as the procedure run plan name until it's renamed at promotion with
  // a name template set on the study schema
  assert(
    flowchartOrProxyVersion == null || !isInaccessible(flowchartOrProxyVersion),
    'Expected flowchart or proxy version to be accessible.'
  );
  const procedureRunPlanName = getProcedureRunPlanNameFromTemplateOrProxyVersion(flowchartOrProxyVersion);
  return {
    id: procedureRunPlan.id,
    name: procedureRunPlanName,
    description: procedureRunPlan.description,
    createdAt: procedureRunPlan.createdAt,
  };
}
